import { supabase } from './supabaseClient';
import { UserProfile, Booking } from './supabaseExtended';

// Types per statistiche admin
export interface AdminStats {
  totalUsers: number;
  totalBookings: number;
  totalRevenue: number;
  pendingVerifications: number;
  activeBookings: number;
  completedBookings: number;
  totalAccommodations: number;
  recentRegistrations: number;
}

export interface UserWithProfile {
  id: string;
  email: string;
  created_at: string;
  email_confirmed_at?: string;
  last_sign_in_at?: string;
  profile?: UserProfile;
}

export interface BookingWithDetails extends Booking {
  accommodation?: {
    title_it: string;
    title_en: string;
    location_it: string;
    location_en: string;
    image: string;
  };
  user_profile?: {
    full_name: string;
    phone_number: string;
    email_verified: boolean;
    phone_verified: boolean;
  };
}

// FUNZIONI STATISTICHE ADMIN

// Ottieni statistiche generali del sistema
export const getAdminStats = async (): Promise<AdminStats> => {
  try {
    // Query parallele per performance
    const [
      usersResult,
      bookingsResult,
      accommodationsResult,
      pendingVerificationsResult,
      recentRegistrationsResult
    ] = await Promise.all([
      // Totale utenti
      supabase
        .from('user_profiles')
        .select('id', { count: 'exact', head: true }),
      
      // Prenotazioni e revenue
      supabase
        .from('bookings')
        .select('status, total_price, created_at'),
      
      // Totale alloggi
      supabase
        .from('accommodations')
        .select('id', { count: 'exact', head: true }),
      
      // Verifiche pendenti
      supabase
        .from('user_profiles')
        .select('id', { count: 'exact', head: true })
        .eq('identity_verified', false),
      
      // Registrazioni recenti (ultimi 30 giorni)
      supabase
        .from('user_profiles')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
    ]);

    // Calcola statistiche bookings
    const bookings = bookingsResult.data || [];
    const totalRevenue = bookings.reduce((sum, booking) => sum + booking.total_price, 0);
    const activeBookings = bookings.filter(b => b.status === 'confirmed').length;
    const completedBookings = bookings.filter(b => b.status === 'completed').length;

    return {
      totalUsers: usersResult.count || 0,
      totalBookings: bookings.length,
      totalRevenue,
      pendingVerifications: pendingVerificationsResult.count || 0,
      activeBookings,
      completedBookings,
      totalAccommodations: accommodationsResult.count || 0,
      recentRegistrations: recentRegistrationsResult.count || 0
    };
  } catch (error) {
    console.error('Errore statistiche admin:', error);
    return {
      totalUsers: 0,
      totalBookings: 0,
      totalRevenue: 0,
      pendingVerifications: 0,
      activeBookings: 0,
      completedBookings: 0,
      totalAccommodations: 0,
      recentRegistrations: 0
    };
  }
};

// GESTIONE UTENTI

// Ottieni tutti gli utenti con profili
export const getAllUsers = async (
  page: number = 1,
  limit: number = 20,
  search?: string
): Promise<{ users: UserWithProfile[]; total: number }> => {
  try {
    let query = supabase
      .from('user_profiles')
      .select(`
        *,
        users:id (
          email,
          created_at,
          email_confirmed_at,
          last_sign_in_at
        )
      `);

    // Filtro di ricerca
    if (search) {
      query = query.or(`full_name.ilike.%${search}%,phone_number.ilike.%${search}%`);
    }

    // Paginazione
    const from = (page - 1) * limit;
    const to = from + limit - 1;

    const { data, error, count } = await query
      .range(from, to)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Trasforma i dati per il formato atteso
    const users: UserWithProfile[] = (data || []).map(profile => ({
      id: profile.id,
      email: profile.users?.email || '',
      created_at: profile.users?.created_at || profile.created_at,
      email_confirmed_at: profile.users?.email_confirmed_at,
      last_sign_in_at: profile.users?.last_sign_in_at,
      profile
    }));

    return { users, total: count || 0 };
  } catch (error) {
    console.error('Errore recupero utenti:', error);
    return { users: [], total: 0 };
  }
};

// Ottieni dettagli utente specifico
export const getUserDetails = async (userId: string): Promise<UserWithProfile | null> => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select(`
        *,
        users:id (
          email,
          created_at,
          email_confirmed_at,
          last_sign_in_at
        ),
        bookings:user_id (
          id,
          status,
          total_price,
          created_at,
          check_in,
          check_out
        ),
        uploaded_documents:user_id (
          document_type,
          file_url,
          created_at
        )
      `)
      .eq('id', userId)
      .single();

    if (error) throw error;

    return {
      id: data.id,
      email: data.users?.email || '',
      created_at: data.users?.created_at || data.created_at,
      email_confirmed_at: data.users?.email_confirmed_at,
      last_sign_in_at: data.users?.last_sign_in_at,
      profile: data
    };
  } catch (error) {
    console.error('Errore dettagli utente:', error);
    return null;
  }
};

// Aggiorna stato verifica utente
export const updateUserVerification = async (
  userId: string,
  updates: {
    email_verified?: boolean;
    phone_verified?: boolean;
    identity_verified?: boolean;
    verification_status?: 'pending' | 'approved' | 'rejected';
  }
) => {
  try {
    const { error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', userId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Errore aggiornamento verifica:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Errore aggiornamento' };
  }
};

// GESTIONE PRENOTAZIONI

// Ottieni tutte le prenotazioni con dettagli
export const getAllBookings = async (
  page: number = 1,
  limit: number = 20,
  status?: string
): Promise<{ bookings: BookingWithDetails[]; total: number }> => {
  try {
    let query = supabase
      .from('bookings')
      .select(`
        *,
        accommodation:accommodation_id (
          title_it,
          title_en,
          location_it,
          location_en,
          image
        ),
        user_profile:user_id (
          full_name,
          phone_number,
          email_verified,
          phone_verified
        )
      `);

    // Filtro per status
    if (status && status !== 'all') {
      query = query.eq('status', status);
    }

    // Paginazione
    const from = (page - 1) * limit;
    const to = from + limit - 1;

    const { data, error, count } = await query
      .range(from, to)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return { bookings: data || [], total: count || 0 };
  } catch (error) {
    console.error('Errore recupero prenotazioni:', error);
    return { bookings: [], total: 0 };
  }
};

// Aggiorna status prenotazione
export const updateBookingStatus = async (
  bookingId: string,
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed',
  notes?: string
) => {
  try {
    const updates: any = { status };
    if (notes) updates.host_notes = notes;

    const { error } = await supabase
      .from('bookings')
      .update(updates)
      .eq('id', bookingId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Errore aggiornamento prenotazione:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Errore aggiornamento' };
  }
};

// GESTIONE VERIFICHE DOCUMENTI

// Ottieni documenti in attesa di verifica
export const getPendingDocuments = async (): Promise<any[]> => {
  try {
    const { data, error } = await supabase
      .from('uploaded_documents')
      .select(`
        *,
        user_profile:user_id (
          full_name,
          email,
          phone_number,
          identity_verified
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Errore documenti pendenti:', error);
    return [];
  }
};

// GESTIONE EMAIL E NOTIFICHE

// Ottieni log email
export const getEmailLogs = async (
  page: number = 1,
  limit: number = 20
): Promise<{ emails: any[]; total: number }> => {
  try {
    const from = (page - 1) * limit;
    const to = from + limit - 1;

    const { data, error, count } = await supabase
      .from('email_notifications')
      .select(`
        *,
        user_profile:user_id (
          full_name,
          email
        )
      `)
      .range(from, to)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { emails: data || [], total: count || 0 };
  } catch (error) {
    console.error('Errore log email:', error);
    return { emails: [], total: 0 };
  }
};

// Invia email personalizzata
export const sendCustomEmail = async (
  userIds: string[],
  subject: string,
  content: string
) => {
  try {
    // Get user emails
    const { data: users, error } = await supabase
      .from('user_profiles')
      .select('id, email')
      .in('id', userIds);

    if (error) throw error;

    // Create email notifications
    const notifications = users?.map(user => ({
      user_id: user.id,
      type: 'admin_custom',
      recipient_email: user.email,
      subject,
      html_content: content,
      text_content: content.replace(/<[^>]*>/g, ''), // Strip HTML for text version
      status: 'pending'
    })) || [];

    const { error: insertError } = await supabase
      .from('email_notifications')
      .insert(notifications);

    if (insertError) throw insertError;

    // TODO: Trigger actual email sending
    return { success: true, sent: notifications.length };
  } catch (error) {
    console.error('Errore invio email personalizzata:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Errore invio' };
  }
};

// UTILITY E EXPORT

// Verifica se l'utente è admin
export const isUserAdmin = async (): Promise<boolean> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // TODO: Implementare logica admin (role-based o whitelist email)
    // Per ora usiamo una whitelist di email admin
    const adminEmails = [
      'admin@j-o-h.com',
      // Aggiungi altre email admin qui
    ];

    return adminEmails.includes(user.email || '');
  } catch (error) {
    console.error('Errore verifica admin:', error);
    return false;
  }
};

// Export dati per CSV
export const exportUsersToCSV = async (): Promise<string> => {
  try {
    const { users } = await getAllUsers(1, 1000); // Get all users
    
    const csvHeader = 'ID,Email,Nome,Telefono,Nazionalità,Email Verificata,Telefono Verificato,Identità Verificata,Data Registrazione\n';
    const csvRows = users.map(user => [
      user.id,
      user.email,
      user.profile?.full_name || '',
      user.profile?.phone_number || '',
      user.profile?.nationality || '',
      user.profile?.email_verified ? 'Sì' : 'No',
      user.profile?.phone_verified ? 'Sì' : 'No',
      user.profile?.identity_verified ? 'Sì' : 'No',
      new Date(user.created_at).toLocaleDateString('it-IT')
    ].map(field => `"${field}"`).join(','));

    return csvHeader + csvRows.join('\n');
  } catch (error) {
    console.error('Errore export CSV:', error);
    return '';
  }
};

export const exportBookingsToCSV = async (): Promise<string> => {
  try {
    const { bookings } = await getAllBookings(1, 1000); // Get all bookings
    
    const csvHeader = 'ID,Ospite,Email,Alloggio,Check-in,Check-out,Ospiti,Totale,Status,Data Prenotazione\n';
    const csvRows = bookings.map(booking => [
      booking.id,
      booking.guest_name,
      booking.guest_email,
      booking.accommodation?.title_it || 'N/A',
      booking.check_in,
      booking.check_out,
      booking.guests_count.toString(),
      `€${booking.total_price.toFixed(2)}`,
      booking.status,
      new Date(booking.created_at).toLocaleDateString('it-IT')
    ].map(field => `"${field}"`).join(','));

    return csvHeader + csvRows.join('\n');
  } catch (error) {
    console.error('Errore export bookings CSV:', error);
    return '';
  }
}; 